/**
 * Generated track thumbnail: a top-down plan of the compiled level as SVG.
 * No WebGL context is needed, so every track (built-in or local) gets one
 * instantly. Footprints are black shapes outlined by kind, as in the game,
 * drawn lowest first; lethal outlines are red and dashed (readable without
 * colour). The start is a ring, the finish a square, checkpoints diamonds. Built
 * with DOM calls only: track text never becomes markup.
 */
import { eulerDegToQuat, type LevelData, type SolidDef } from '../world/LevelData';
import { BUILTIN_MATERIALS, type SurfaceKind } from '../world/SurfaceMaterials';

const NS = 'http://www.w3.org/2000/svg';
/** Solids drawn at most (the largest first); a plan does not need every tile. */
const MAX_SHAPES = 1500;
const VIEW = 100;

type Pt = [number, number];

interface Footprint {
  points: Pt[];
  top: number;
  kind: SurfaceKind;
}

function el<K extends keyof SVGElementTagNameMap>(name: K, attrs: Record<string, string | number>): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, name);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

/** World XZ outline and top height of one solid. */
export function footprint(s: SolidDef, kind: SurfaceKind): Footprint {
  const q = eulerDegToQuat(s.rotationDeg, { x: 0, y: 0, z: 0, w: 1 });
  const rot = (x: number, y: number, z: number): [number, number, number] => {
    // v' = v + w t + q × t, with t = 2 (q × v)
    const tx = 2 * (q.y * z - q.z * y);
    const ty = 2 * (q.z * x - q.x * z);
    const tz = 2 * (q.x * y - q.y * x);
    return [x + q.w * tx + (q.y * tz - q.z * ty), y + q.w * ty + (q.z * tx - q.x * tz), z + q.w * tz + (q.x * ty - q.y * tx)];
  };
  const [px, py, pz] = s.position;
  let corners: [number, number, number][];
  if (s.shape.type === 'cylinder') {
    const r = s.shape.radius;
    const h = s.shape.halfHeight;
    corners = [];
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2;
      corners.push([Math.cos(a) * r, h, Math.sin(a) * r], [Math.cos(a) * r, -h, Math.sin(a) * r]);
    }
  } else {
    const [hx, hy, hz] = s.shape.halfExtents;
    corners = [];
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) corners.push([sx * hx, sy * hy, sz * hz]);
  }
  const world = corners.map(([x, y, z]) => {
    const [rx, ry, rz] = rot(x, y, z);
    return [rx + px, ry + py, rz + pz] as const;
  });
  return { points: hull(world.map(([x, , z]) => [x, z] as Pt)), top: Math.max(...world.map((w) => w[1])), kind };
}

/** Convex hull (monotone chain). */
function hull(pts: Pt[]): Pt[] {
  const p = [...pts].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o: Pt, a: Pt, b: Pt): number => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Pt[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: Pt[] = [];
  for (const q of p.reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, q) <= 0) upper.pop();
    upper.push(q);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

const area = (pts: Pt[]): number => Math.abs(pts.reduce((s, p, i) => s + p[0] * pts[(i + 1) % pts.length]![1] - pts[(i + 1) % pts.length]![0] * p[1], 0) / 2);

/** Builds the plan. `title` becomes the SVG's accessible name. */
export function trackThumbnail(level: LevelData, title: string): SVGSVGElement {
  const kinds = new Map<string, SurfaceKind>([...BUILTIN_MATERIALS, ...(level.materials ?? [])].map((m) => [m.id, m.kind]));
  const prints = level.solids
    .map((s) => footprint(s, kinds.get(s.material) ?? 'safe'))
    .map((f) => ({ ...f, a: area(f.points) }))
    .sort((a, b) => b.a - a.a)
    .slice(0, MAX_SHAPES)
    .sort((a, b) => a.top - b.top);
  const markers = level.triggers.map((t) => ({ kind: t.kind, x: t.center[0], z: t.center[2] }));
  const xs = [...prints.flatMap((f) => f.points.map((p) => p[0])), ...markers.map((m) => m.x)];
  const zs = [...prints.flatMap((f) => f.points.map((p) => p[1])), ...markers.map((m) => m.z)];
  const minX = Math.min(...xs, 0);
  const maxX = Math.max(...xs, 1);
  const minZ = Math.min(...zs, 0);
  const maxZ = Math.max(...zs, 1);
  const span = Math.max(maxX - minX, maxZ - minZ, 1);
  const pad = 6;
  const k = (VIEW - pad * 2) / span;
  const ox = pad + (VIEW - pad * 2 - (maxX - minX) * k) / 2;
  const oz = pad + (VIEW - pad * 2 - (maxZ - minZ) * k) / 2;
  const sx = (x: number): number => +(ox + (x - minX) * k).toFixed(2);
  const sz = (z: number): number => +(oz + (z - minZ) * k).toFixed(2);

  const svg = el('svg', { viewBox: `0 0 ${VIEW} ${VIEW}`, role: 'img', class: 'track-thumb' });
  const label = el('title', {});
  label.textContent = `Plan of ${title}`;
  svg.append(label);
  for (const f of prints) {
    if (f.points.length < 3) continue;
    // Drawn like the game: black shapes outlined by kind (stroke colours in app.css), higher shapes over lower ones.
    svg.append(el('polygon', { points: f.points.map((p) => `${sx(p[0])},${sz(p[1])}`).join(' '), class: `thumb-solid thumb-${f.kind}` }));
  }
  for (const m of markers) {
    const x = sx(m.x);
    const z = sz(m.z);
    if (m.kind === 'start') svg.append(el('circle', { cx: x, cy: z, r: 2.6, class: 'thumb-start' }));
    else if (m.kind === 'finish') svg.append(el('rect', { x: x - 2.4, y: z - 2.4, width: 4.8, height: 4.8, class: 'thumb-finish' }));
    else svg.append(el('polygon', { points: `${x},${z - 2} ${x + 2},${z} ${x},${z + 2} ${x - 2},${z}`, class: 'thumb-checkpoint' }));
  }
  return svg;
}
